import { FC } from "react";
import { Content } from "@prismicio/client";
import { SliceComponentProps } from "@prismicio/react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import Testimonial from "@/components/atoms/testimonials/Testimonial";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { __ } from "@/lib/i18n/functions";
import { appLocales, Locales } from "@/lib/i18n";

export type TestimonialSliderProps = SliceComponentProps<Content.TestimonialSliderSlice> & {
  context?: { locale?: Locales };
};

const TestimonialSlider: FC<TestimonialSliderProps> = ({ slice, context }) => {
  const locale = context?.locale || appLocales.defaultLocale;
  const { title, testimonials } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      aria-labelledby={__("Testimonials", locale)}
      className="bg-[#F7F9F980]"
    >
      <div className="container space-y-7.5 px-4 py-10">
        <h2 id="testimonial" className="font-cabinet-grotesk text-center text-3xl font-medium md:text-[40px]">
          {title}
        </h2>

        <Carousel aria-label="Testimonial Carousel">
          <CarouselContent className="-ml-3 lg:-ml-6 xl:-ml-7.5" role="list">
            {testimonials &&
              testimonials.length >= 1 &&
              testimonials.map(({ quote, image, author, photo, rating }, index) => {
                return (
                  <CarouselItem
                    key={index}
                    className="basis-full pl-3 sm:basis-1/2 lg:basis-1/3 lg:pl-6 xl:basis-1/4 xl:pl-7.5"
                  >
                    <Testimonial
                      variant={image.url ? "photoWithQuote" : "default"}
                      mainImage={image.url || ""}
                      quote={quote}
                      photographer={author || ""}
                      photographerImage={photo.url || ""}
                      rating={rating as number}
                      className="max-w-full"
                    />
                  </CarouselItem>
                );
              })}
          </CarouselContent>

          <div className="flex items-center justify-center gap-4 max-sm:mt-4">
            <CarouselPrevious
              variant="light"
              className="sm:bg-pink-primary sm:hover:pink-overlay border-primary-light/50 size-11 rounded-none border bg-white text-black disabled:opacity-50 sm:absolute sm:top-[40%] sm:left-0 sm:border-transparent sm:text-white"
              aria-label="Go to previous testimonial"
            >
              <ArrowLeft className="size-5" />
            </CarouselPrevious>
            <CarouselNext
              variant="light"
              className="sm:bg-pink-primary border-primary-light/50 sm:hover:pink-overlay size-11 rounded-none border bg-white text-black disabled:opacity-50 sm:absolute sm:top-[40%] sm:right-0 sm:border-none sm:border-transparent sm:text-white"
              aria-label="Go to next testimonial"
            >
              <ArrowRight className="size-5" />
            </CarouselNext>
          </div>
        </Carousel>
      </div>
    </section>
  );
};

export default TestimonialSlider;
